/* biome-ignore-all lint/a11y/useAnchorContent: existing functionality */
/* biome-ignore-all lint/style/useNamingConvention: allow camel case */
'use client';

import PropTypes from 'prop-types';
import {Suspense, useState} from 'react';
import {Button} from '../../atoms/Button';
import {Checkbox} from '../../atoms/Checkbox';
import {InputText} from '../../atoms/InputText';
import {Link} from '../../atoms/Link';
import {Spinner} from '../../atoms/Spinner';
import {Typography} from '../../atoms/Typography';
import {useMatchQuery} from '../../hooks/useMatchQuery';
import {CloseLineIcon} from '../../svg/CloseLineIcon';
import {saveUserDataToLocalStorage, submitRegistrationWithCaptcha} from '../../utils/auth/utils';

import '../LoginModal/LoginContainer.sass';
import './SignupModal.sass';

export const SignUpModalContent = (props) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [password, setPassword] = useState('');
  const [marketingCheckboxChecked, setMarketingCheckboxChecked] = useState(true);
  const params = new URLSearchParams(window.location.search);
  const nextParam = params.get('next')?.replace(/\/+$/, '');
  const proSignUp = nextParam === '/checkout/pro-subscription';

  const [errors, setErrors] = useState({});

  const onMarketingCheckboxClick = () => {
    setMarketingCheckboxChecked(!marketingCheckboxChecked);
  };

  const isStJudeAccount = (userEmail) => userEmail.endsWith('@alsac.stjude.org');

  const onSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!confirmEmail) {
      newErrors.confirm_email = [{message: 'Confirm Email is a required field.'}];
    }
    if (confirmEmail && email !== confirmEmail) {
      newErrors.email = [{message: 'Confirm Email must match Email.'}];
    }
    if (!email) {
      newErrors.email = [{message: 'Email is a required field.'}];
    }
    if (!password) {
      newErrors.password = [{message: 'Password is a required field.'}];
    }
    if (!firstName) {
      newErrors.first_name = [
        {
          message: 'First Name is a required field',
        },
      ];
    }
    if (firstName.length > 60) {
      newErrors.first_name_length = [
        {
          message: 'First Name must be shorter than 60 characters',
          code: 'max_length',
        },
      ];
    }
    if (email.length > 60) {
      newErrors.email_length = [
        {
          message:
            "You have reached the maximum amount of characters. Please edit your 'email' field to be less than 60 characters.",
          code: 'max_length',
        },
      ];
    }
    if (!lastName) {
      newErrors.last_name = [
        {
          message: 'Last Name is a required field',
        },
      ];
    }
    if (lastName.length > 60) {
      newErrors.last_name_length = [
        {
          message: 'Last Name must be shorter than 60 characters',
          code: 'max_length',
        },
      ];
    }

    if (password.length < 6 || password.length > 128) {
      const minLen = password.length < 6;
      newErrors.password_length = [
        {
          message: `Password must be at ${minLen ? 'least 6' : 'most 128'} characters`,
          code: minLen ? 'min_length' : 'max_length',
        },
      ];
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const body = {
      email,
      confirm_email: confirmEmail,
      password,
      first_name: firstName,
      last_name: lastName,
      email_newsletter: marketingCheckboxChecked,
    };

    try {
      const response = await submitRegistrationWithCaptcha(body);
      const jsonResponse = await response.json();
      if (!response.ok) {
        evite.error('Submit registration failed', new Error('Failed to submit registration'), {
          errors: jsonResponse.error.__all__,
        });
        setErrors({email: jsonResponse.error.__all__});
        return;
      }

      // TODO: request.ads.track_gtm_event("register", {"register_type": "evite"});

      saveUserDataToLocalStorage(jsonResponse);
      const userId = jsonResponse.user_id;
      // TODO: make parameters to onSuccess the same as for LoginModal onSuccess prop
      props.onSuccess(userId, `${firstName} ${lastName}`, '', jsonResponse.email);
    } catch {
      evite.error(new Error('Failed to submit registration'));
    }
  };

  const {matches: largeScreen} = useMatchQuery('(min-width: 80rem)');

  return (
    <div className="meganav-login-modal">
      <div className="logo-header">
        {!props.isModal && !proSignUp ? (
          <a
            id="logo_top"
            href="/"
            className="logo evite-logo"
            data-qa-id="evite-logo"
            aria-label="evite home"
          />
        ) : // biome-ignore lint/style/noNestedTernary: existing functionality; refactor needed
        proSignUp ? (
          <div className="modal-header__pro-signup">
            <Typography
              as="h2"
              className="modal-header__pro-signup__guiding-text"
              variant={largeScreen ? 'header1' : 'header2'}
            >
              Create an account
            </Typography>
            <Typography
              as="p"
              className="modal-header__pro-signup__guiding-text"
              variant={largeScreen ? 'paragraph2' : 'paragraph3'}
            >
              Sign up now to get all the benefits of Evite Pro.
            </Typography>
          </div>
        ) : (
          <span className="label1 large">Sign Up</span>
        )}
        {props.isModal && (
          <button
            className="unstyled-button"
            onClick={props.onCloseModal}
            data-qa-id="signupModalCloseButton"
            aria-label="close sign up"
            type="button"
          >
            <CloseLineIcon />
          </button>
        )}
      </div>
      <div className="modal-body">
        {(!props.lgWindow || props.isModal) && !proSignUp ? (
          <Typography
            as="h2"
            variant={largeScreen ? 'header1' : 'header2'}
            data-qa-id="signupModalCreateAccountHeader"
          >
            Join the party! Create an account.
          </Typography>
        ) : (
          ''
        )}
        <div className="social-login-group">
          <Button
            variant="secondary"
            size="large"
            onClick={(e) => props.onGoogleLoginClick(e, marketingCheckboxChecked)}
            id="google"
            data-qa-id="signupModalSocialGoogleLoginButton"
          >
            <span />
            Sign in with Google
          </Button>
          <Button
            variant="secondary"
            size="large"
            onClick={(e) => props.onAppleLoginClick(e, marketingCheckboxChecked)}
            id="apple"
            data-qa-id="signupModalSocialAppleLoginButton"
          >
            <span />
            Sign in with Apple
          </Button>
        </div>
        <form className="login-form" onSubmit={onSubmit}>
          <Typography as="label" variant="label2" size="small" data-qa-id="orSignUpWithEmailText">
            Or Sign Up with Email
          </Typography>
          <div className="name-group">
            <InputText
              value={firstName}
              type="text"
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="First Name"
              error={
                (errors.first_name && errors.first_name[0].message) ||
                (errors.first_name_length && errors.first_name_length[0].message)
              }
              data-qa-id="signupModalFirstNameInput"
            />
            <InputText
              value={lastName}
              type="text"
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Last Name"
              error={
                (errors.last_name && errors.last_name[0].message) ||
                (errors.last_name_length && errors.last_name_length[0].message)
              }
              data-qa-id="signupModalLastNameInput"
            />
          </div>
          <InputText
            value={email}
            type="email"
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            error={
              (errors.email && errors.email[0].message) ||
              (errors.email_length && errors.email_length[0].message)
            }
            data-qa-id="signupModalEmailInput"
          />
          <InputText
            value={confirmEmail}
            type="email"
            onChange={(e) => setConfirmEmail(e.target.value)}
            placeholder="Confirm Email"
            error={errors.confirm_email && errors.confirm_email[0].message}
            data-qa-id="signupModalConfirmEmailInput"
          />
          <InputText
            value={password}
            type="password"
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            error={
              (errors.password && errors.password[0].message) ||
              (errors.password_length && errors.password_length[0].message)
            }
            data-qa-id="signupModalPasswordInput"
          />
          <div className="marketing-checkbox-wrapper">
            <Checkbox
              label={
                <span className="marketing-checkbox-text">
                  Get America&apos;s trendiest parties delivered right to my inbox.
                </span>
              }
              onChange={onMarketingCheckboxClick}
              checked={marketingCheckboxChecked}
              data-qa-id="signupModalMarketingCheckbox"
            />
          </div>
          <Button
            variant="primary"
            size="large"
            type="submit"
            data-qa-id="signupModalSignupSubmitButton"
          >
            Sign Up
          </Button>
          {!isStJudeAccount(email) && (
            <Typography
              variant="paragraph4"
              className="signup-terms-disclaimer"
              data-qa-id="signupTermsDisclaimerText"
            >
              By filling in the forms above and clicking the &quot;Sign Up&quot; button you accept
              and agree to Evite&apos;s{' '}
              <Link href={props.privacyPolicyHref} data-qa-id="privacyPolicyLink">
                <Typography
                  as="span"
                  variant="paragraph4"
                  color="tanzanite"
                  className="register-terms__link"
                >
                  Privacy Policy
                </Typography>
              </Link>{' '}
              and{' '}
              <Link href={props.termsHref} data-qa-id="termsOfServiceLink">
                <Typography
                  as="span"
                  variant="paragraph4"
                  color="tanzanite"
                  className="register-terms__link"
                >
                  Terms of Service
                </Typography>
              </Link>
              .
            </Typography>
          )}
        </form>
        <Typography
          variant="paragraph2"
          className="sign-in-redirect"
          data-qa-id="alreadyHaveAccountText"
        >
          Have an account?{' '}
          <button
            className="unstyled-button link"
            onClick={props.onSignInClick}
            data-qa-id="signupModalGoToSignInButton"
            type="button"
          >
            Sign in
          </button>
        </Typography>
      </div>
    </div>
  );
};

SignUpModalContent.propTypes = {
  onCloseModal: PropTypes.func,
  lgWindow: PropTypes.bool,
  onSuccess: PropTypes.func,
  onSignInClick: PropTypes.func,
  privacyPolicyHref: PropTypes.string,
  termsHref: PropTypes.string,
  onGoogleLoginClick: PropTypes.func,
  onFacebookLoginClick: PropTypes.func,
  onAppleLoginClick: PropTypes.func,
};

export const SignUpModal = (props) => (
  <Suspense fallback={<Spinner />}>
    <SignUpModalContent {...props} />
  </Suspense>
);
